/**
 * Extraction du body texte/HTML d'un message Gmail à partir de son arbre MIME.
 *
 * Gmail renvoie payload comme un arbre récursif. Pour un mail typique :
 *   payload.mimeType = "multipart/alternative"
 *   payload.parts = [
 *     { mimeType: "text/plain", body: { data: "base64..." } },
 *     { mimeType: "text/html",  body: { data: "base64..." } }
 *   ]
 * Pour un mail avec attachments :
 *   payload.mimeType = "multipart/mixed"
 *   payload.parts = [{multipart/alternative...}, {image/png attachmentId...}, ...]
 *
 * On walk récursif, on garde le premier text/html (préféré) et text/plain.
 */

import type { GmailMessage, GmailMessagePayload } from "./api";

/**
 * Decode base64url → string (UTF-8 safe via TextDecoder).
 * Gmail utilise base64url (- au lieu de +, _ au lieu de /, pas de padding).
 */
function decodeBase64Url(b64: string): string {
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const std = padded.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

/** Walk récursif, retourne le data du premier part matchant mimeType. */
function findPart(part: GmailMessagePayload, mimeType: string): string | null {
  if (part.mimeType === mimeType && part.body?.data) {
    return decodeBase64Url(part.body.data);
  }
  for (const sub of part.parts ?? []) {
    const found = findPart(sub, mimeType);
    if (found) return found;
  }
  return null;
}

export interface MessageBody {
  html: string | null;
  text: string | null;
}

/** Extrait html + plain text du payload d'un message. Both can be null. */
export function extractBody(message: GmailMessage): MessageBody {
  if (!message.payload) return { html: null, text: null };
  return {
    html: findPart(message.payload, "text/html"),
    text: findPart(message.payload, "text/plain"),
  };
}

/** Convertit du plain text en HTML simple — fallback quand pas de body html. */
export function plainToHtml(plain: string): string {
  return plain
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line) || "&nbsp;"}</p>`)
    .join("");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return map[c] ?? c;
  });
}

// ─── Attachments ─────────────────────────────────────────────────────────────

export interface MessageAttachment {
  /** Identifiant Gmail pour fetch via attachments.get */
  attachmentId: string;
  filename: string;
  mimeType: string;
  /** Bytes (Gmail-reported, peut être 0 pour très petits inline) */
  size: number;
}

/**
 * Walk le payload, retourne les vraies pièces jointes (= pas d'inline images
 * cid: qui font partie du body).
 *
 * Critères d'une PJ "vraie" :
 *   - body.attachmentId présent (Gmail stocke séparément)
 *   - filename non vide
 *   - Content-Disposition != "inline" (ou pas de Content-ID header)
 */
export function extractAttachments(message: GmailMessage): MessageAttachment[] {
  if (!message.payload) return [];
  const out: MessageAttachment[] = [];
  walkParts(message.payload, (part) => {
    if (!part.body?.attachmentId || !part.filename) return;
    if (isInlinePart(part)) return;
    out.push({
      attachmentId: part.body.attachmentId,
      filename: part.filename,
      mimeType: part.mimeType ?? "application/octet-stream",
      size: part.body.size ?? 0,
    });
  });
  return out;
}

function walkParts(
  part: GmailMessagePayload,
  visit: (p: GmailMessagePayload) => void,
): void {
  visit(part);
  for (const sub of part.parts ?? []) walkParts(sub, visit);
}

function isInlinePart(part: GmailMessagePayload): boolean {
  const headers = part.headers ?? [];
  const cd = headers.find((h) => h.name.toLowerCase() === "content-disposition");
  if (cd && /^\s*inline/i.test(cd.value)) return true;
  // Cid (image embarquée référencée par <img src="cid:...">) → inline
  const cid = headers.find((h) => h.name.toLowerCase() === "content-id");
  if (cid) return true;
  return false;
}

/**
 * Decode base64url → Uint8Array<ArrayBuffer> (binary data, pour Blob).
 * Construction explicite sur ArrayBuffer (pas ArrayBufferLike) pour matcher
 * la signature de Blob constructor en TS 5.7+.
 */
export function base64UrlToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const std = padded.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std);
  const buffer = new ArrayBuffer(bin.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
